import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { routes } from "../../routes/routes";
import { useRole } from "../../context/RoleContext";
import { useBrandConfig } from "../../queries/shared";
import { cartDataMapper } from "../../utils/cartDataMapper";
import { addItem } from "../../redux/features/slices/cartSlice";
import type { NavbarProps } from "./MinimalNavBar";

import { getExcludedPaths } from "../../routes/LayoutExcludedRoutes";

import Navbar from "./NavBar";
import colors from "../../theme/Colours";
import SellerNavbar from "./SellerNavbar";
import SalesRepNavbar from "./SalesRepNavBar";
import MobileNavbar from "./MobileNavbar";
import MinimalNavBar from "./MinimalNavBar";
import useWindowWidth from "../homePage/WindowWidth";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import { useCart } from "../../queries/cart";
import { normalizeRole } from "../../utils/roleUtils";

const isRolePath = (pathname: string, rolePath: string) =>
  pathname === `/${rolePath}` || pathname.startsWith(`/${rolePath}/`);

const getMinimalNavbar = (variant: "signup" | "signin" | "logoOnly") => {
  const Component = (props: NavbarProps) => (
    <MinimalNavBar {...props} variant={variant} />
  );

  Component.displayName = `MinimalNavbarWith${variant[0].toUpperCase()}${variant.slice(1)}`;
  return Component;
};

const calculatePadding = (screenWidth: number) => {
  const paddingMap = [
    { min: 1024, max: 1280, padding: 64 },
    { min: 1280, max: 1440, padding: 160 },
    { min: 1440, max: 1512, padding: 180 },
    { min: 1512, max: 1728, padding: 189 },
    { min: 1728, max: Infinity, padding: 216 },
  ];

  return (
    paddingMap.find(({ min, max }) => screenWidth >= min && screenWidth <= max)
      ?.padding ?? 64
  );
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);
  const searchParams = new URLSearchParams(location.search);
  const signinUserType = searchParams.get("type") as
    | "maltora-seller"
    | "maltora-customer"
    | "maltora-sales-rep"
    | null;
  const windowWidth = useWindowWidth();
  const orgId = localStorage.getItem("orgId");
  const { role, isRoleLoading } = useRole();
  const { data: brandConfig } = useBrandConfig();
  const { data: cartData } = useCart();
  const isMobile = useIsMobile();

  const isSeller = useMemo(
    () =>
      normalizeRole(role) === "maltora-seller" ||
      isRolePath(location.pathname, "maltora-seller"),
    [location.pathname, role]
  );

  useEffect(() => {
    if (role === "maltora-sales-rep" || role === "maltora-seller") return;
    if (cartData?.Products) {
      const items = cartDataMapper(cartData.Products);
      dispatch(addItem(items));
    }
  }, [cartData, dispatch]);

  const excludedPaths = useMemo(() => getExcludedPaths(), []);

  const NavbarComponent = useMemo(() => {
    const currentPath = location.pathname;

    if (
      isRoleLoading &&
      !currentPath.includes("/signin") &&
      !currentPath.includes("/signup")
    ) {
      return null;
    }

    const effectiveRole = normalizeRole(
      role || localStorage.getItem("userRole") || ""
    );
    const minimalNavPathsWithSignUp = [
      `/${routes.public.signIn}`,
      `/${routes.public.signIn}`,
    ];
    const minimalNavPathsWithSignIn = [
      `/${routes.public.signUp}`,
      `/${routes.public.signUp}`,
    ];
    const minimalNavPathsLogoOnly = [
      `/${routes.public.forgotPassword}`,
      `/${routes.public.setPassword}`,
      `/${routes.public.resetConfirm}`,
      `/${routes.public.signUpConfirm}`,
      `/${routes.public.forgotPassword}`,
      `/${routes.public.setPassword}`,
      `/${routes.public.resetConfirm}`,
      `/${routes.public.signUpConfirm}`,
    ];

    // Hide navbar entirely on signin, signup, forgot-password, and set-password pages
    if (
      minimalNavPathsWithSignUp.includes(currentPath) ||
      minimalNavPathsWithSignIn.includes(currentPath) ||
      currentPath === `/${routes.public.forgotPassword}` ||
      currentPath === `/${routes.public.setPassword}`
    ) {
      return null;
    }

    if (minimalNavPathsLogoOnly.includes(currentPath))
      return getMinimalNavbar("logoOnly");

    if (
      effectiveRole === "maltora-seller" ||
      isRolePath(currentPath, "maltora-seller")
    )
      return SellerNavbar;

    if (
      effectiveRole === "maltora-sales-rep" ||
      isRolePath(currentPath, "maltora-sales-rep")
    )
      return SalesRepNavbar;

    if (
      effectiveRole === "maltora-customer" ||
      isRolePath(currentPath, "maltora-customer")
    )
      return windowWidth > 599 ? Navbar : MobileNavbar;

    if (isRolePath(currentPath, "maltora-seller")) return SellerNavbar;
    if (isRolePath(currentPath, "maltora-sales-rep")) return SalesRepNavbar;
    if (isRolePath(currentPath, "maltora-customer"))
      return windowWidth > 599 ? Navbar : MobileNavbar;

    return Navbar;
  }, [
    brandConfig,
    location.pathname,
    orgId,
    role,
    isRoleLoading,
    signinUserType,
    windowWidth,
  ]);

  const renderLayout = useMemo(() => {
    const isExcludedPath = excludedPaths.includes(location.pathname);
    const minHeight = isSeller ? "calc(100vh - 74px)" : "calc(100vh - 77px)";

    const isAllCustomersOrLedger =
      location.pathname.includes("/maltora-seller/all-customers") ||
      /\/maltora-seller\/ledgers\//.test(location.pathname);

    const horizontalPadding = isAllCustomersOrLedger
      ? 64
      : calculatePadding(windowWidth);
    const padding = isMobile ? "11px" : `${103}px ${horizontalPadding}px`;

    if (isExcludedPath) {
      return <Box component="main">{children}</Box>;
    }

    return (
      <Box sx={{ backgroundColor: colors.appLayoutBG }}>
        <Box
          sx={{
            padding,
            minHeight,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }, [
    children,
    excludedPaths,
    isMobile,
    isSeller,
    location.pathname,
    windowWidth,
  ]);

  return (
    <>
      {NavbarComponent && (
        <NavbarComponent logo={brandConfig?.brand?.smallLogoUrl} />
      )}
      {renderLayout}
    </>
  );
};

export default AppLayout;
